import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardActions, CardContent, Grid2 } from '@mui/material'
import { CardTable } from 'components/datatable/NewTable'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import SimpleTabList from 'components/list/SimpleTabList'
import React from 'react'
import { COLLECTIVITY, STATUS, YEAR } from 'rpqs/constants/RpqsConstant'
import i18n from 'simple-react-i18n'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import PropTypes from 'prop-types'
import { groupBy } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { push } from '@lagunovsky/redux-react-router'
import { getDate, getYearDate } from 'utils/DateUtil'
import { sortCollectivities } from 'rpqs/utils/RPQSUtils'

const FilterFields = () => {
    return (
        <Card>
            <CardContent>
                <Grid2 container spacing={4}>
                    <Grid2 size={3}>
                        <Input
                            tooltip={i18n.searchThenEnter}
                            title={i18n.search}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <Select
                            label={i18n.collectivity}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <Select
                            label={i18n.status}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <Select
                            label={i18n.year}
                        />
                    </Grid2>
                </Grid2>
            </CardContent>

            <CardActions sx={{ display: 'flex', justifyContent: 'end' }}>
                <Button
                    variant='outlined'
                >
                    {i18n.reinit}
                </Button>
                <Button
                    variant='contained'
                >
                    {i18n.search}
                </Button>
            </CardActions>
        </Card>
    )
}

const TablePanel = ({ rows }) => {
    const dispatch = useDispatch()
    const rowsFormated = rows.map((collectivity) => {
        const dateStart = getDate(collectivity.dateStart)
        const dateEnd = getDate(collectivity.dateEnd)
        return { ...collectivity, dateEnd, dateStart }
    })

    return (
        <CardTable
            title={i18n.collectivity}
            headers={['siren', 'name', 'dateStart', 'dateEnd', 'status', 'Documentation']}
            rows={rowsFormated}
            onClickRow={(collectivity) => dispatch(push(`/collectivity/rpqs/${collectivity.id}`))}
        />
    )
}

TablePanel.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.shape({})),
}

const YearPanel = ({ collectivities }) => {
    const groupedCollectivities = groupBy(collectivities, ({ dateEnd }) => getYearDate(dateEnd) || i18n.unknown)

    return (
        <>
            {Object.keys(groupedCollectivities).sort((a, b) => sortCollectivities(a, b, true)).map((year, i) => {
                return (
                    <Accordion key={year} defaultExpanded={i === 0} className='margin-bottom-1'>
                        <AccordionSummary>
                            <span>{year} ({groupedCollectivities[year].length} {i18n.folders})</span>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: 0 }}>
                            <TablePanel rows={groupedCollectivities[year]}/>
                        </AccordionDetails>
                    </Accordion>
                )
            })}
        </>
    )
}

YearPanel.propTypes = {
    collectivities: PropTypes.arrayOf(PropTypes.shape({})),
}

const StatusPanel = ({ collectivities }) => {
    const groupedCollectivities = groupBy(collectivities, ({ status }) => status || i18n.unknown)

    return (
        <>
            {Object.keys(groupedCollectivities).sort((a, b) => sortCollectivities(a, b)).map((status, i) => {
                return (
                    <Accordion key={status} defaultExpanded={i === 0} className='margin-bottom-1'>
                        <AccordionSummary>
                            <span>{status} ({groupedCollectivities[status].length} {i18n.folders})</span>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: 0 }}>
                            <TablePanel rows={groupedCollectivities[status]}/>
                        </AccordionDetails>
                    </Accordion>
                )
            })}
        </>
    )
}

StatusPanel.propTypes = {
    collectivities: PropTypes.arrayOf(PropTypes.shape({})),
}

const CollectivityPanel = ({ collectivities }) => {
    const groupedCollectivities = groupBy(collectivities, ({ name }) => name || i18n.unknown)

    return (
        <>
            {Object.keys(groupedCollectivities).sort((a, b) => sortCollectivities(a, b)).map((collectivity, i) => {
                return (
                    <Accordion key={collectivity} defaultExpanded={i === 0} className='margin-bottom-1'>
                        <AccordionSummary>
                            <span>{collectivity} ({groupedCollectivities[collectivity].length} {i18n.folders})</span>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: 0 }}>
                            <TablePanel rows={groupedCollectivities[collectivity]}/>
                        </AccordionDetails>
                    </Accordion>
                )
            })}
        </>
    )
}

CollectivityPanel.propTypes = {
    collectivities: PropTypes.arrayOf(PropTypes.shape({})),
}

const RpqsDashboardApp = () => {
    const { collectivities } = useSelector((store) => ({
        collectivities: store.CollectivityReducer.collectivities,
    }))

    useTitle(() => [{
        title: 'RPQS',
        href: 'rpqs',
    }, {
        title: i18n.dashboard,
        href: 'rpqs',
    }], [])

    useActions(() => ({
        new: () => {},
        export: () => {},
    }), [])

    return (
        <div style={{ marginTop: '10px', marginRight: '10px' }}>
            <SimpleTabList
                tabs={[
                    {
                        constant: YEAR,
                        label: i18n.year,
                        icon: 'event',
                    },
                    {
                        constant: COLLECTIVITY,
                        label: i18n.collectivity,
                        icon: 'house',
                    },
                    {
                        constant: STATUS,
                        label: i18n.status,
                        icon: 'work',
                    },
                ]}
                headerComponent={<FilterFields/>}
                noPadding
                defaultTab={YEAR}
            >
                {tab => (
                    <div style={{ padding: '10px' }}>
                        {tab === YEAR && (
                            <YearPanel collectivities={collectivities}/>
                        )}
                        {tab === COLLECTIVITY && (
                            <CollectivityPanel collectivities={collectivities}/>
                        )}
                        {tab === STATUS && (
                            <StatusPanel collectivities={collectivities}/>
                        )}
                    </div>
                )}
            </SimpleTabList>
        </div>
    )
}

export default RpqsDashboardApp